import type { IAnalyticsRepository } from '@src/domain/analytics/repository/analytics_repository';
import { TrackPageViewUseCase } from './track_page_view.usecase';
import { TrackEventUseCase } from './track_event.usecase';
import { SetUserPropertiesUseCase } from './set_user_properties.usecase';
import { InitializeAnalyticsUseCase } from './initialize_analytics.usecase';

/**
 * Analytics Service
 * Orchestrates all analytics-related use cases
 * Provides a unified interface for analytics operations
 */
export class AnalyticsService {
    private trackPageViewUseCase: TrackPageViewUseCase;
    private trackEventUseCase: TrackEventUseCase;
    private setUserPropertiesUseCase: SetUserPropertiesUseCase;
    private initializeAnalyticsUseCase: InitializeAnalyticsUseCase;

    constructor(private readonly analyticsRepository: IAnalyticsRepository) {
        this.trackPageViewUseCase = new TrackPageViewUseCase(analyticsRepository);
        this.trackEventUseCase = new TrackEventUseCase(analyticsRepository);
        this.setUserPropertiesUseCase = new SetUserPropertiesUseCase(analyticsRepository);
        this.initializeAnalyticsUseCase = new InitializeAnalyticsUseCase(analyticsRepository);
    }

    /**
     * Initialize analytics system
     */
    async initialize(params: {
        measurementId: string;
        enabled?: boolean;
        debug?: boolean;
        testMode?: boolean;
        options?: {
            cookieFlags?: string;
            cookieDomain?: string;
            cookieExpires?: number;
            anonymizeIp?: boolean;
        };
    }): Promise<void> {
        await this.initializeAnalyticsUseCase.execute(params);
    }

    /**
     * Track a page view
     */
    async trackPageView(params: {
        path: string;
        title: string;
        location?: string;
        metadata?: Record<string, any>;
    }): Promise<void> {
        await this.trackPageViewUseCase.execute(params);
    }

    /**
     * Track a custom event
     */
    async trackEvent(params: {
        category: string;
        action: string;
        label?: string;
        value?: number;
        metadata?: Record<string, any>;
    }): Promise<void> {
        await this.trackEventUseCase.execute(params);
    }

    /**
     * Set user properties
     */
    async setUserProperties(params: {
        userId?: string;
        userRole?: string;
        universityId?: string;
        universityName?: string;
        properties?: Record<string, any>;
    }): Promise<void> {
        await this.setUserPropertiesUseCase.execute(params);
    }

    /**
     * Clear user data (on logout)
     */
    async clearUserData(): Promise<void> {
        await this.analyticsRepository.clearUserData();
    }

    /**
     * Check if analytics is initialized
     */
    isInitialized(): boolean {
        return this.analyticsRepository.isInitialized();
    }

    // Convenience methods for common tracking scenarios

    /**
     * Track Pasco view
     */
    async trackPascoView(pascoId: string, pascoTitle: string, metadata?: Record<string, any>): Promise<void> {
        await this.trackEvent({
            category: 'Pasco',
            action: 'view',
            label: pascoId,
            metadata: {
                pasco_title: pascoTitle,
                ...metadata,
            },
        });
    }

    /**
     * Track Pasco click
     */
    async trackPascoClick(pascoId: string, clickType: string, metadata?: Record<string, any>): Promise<void> {
        await this.trackEvent({
            category: 'Pasco',
            action: 'click',
            label: `${clickType}_${pascoId}`,
            metadata: {
                click_type: clickType,
                pasco_id: pascoId,
                ...metadata,
            },
        });
    }

    /**
     * Track Pepper Bot usage
     */
    async trackPepperBotUsage(action: string, metadata?: Record<string, any>): Promise<void> {
        await this.trackEvent({
            category: 'PepperBot',
            action,
            metadata,
        });
    }

    /**
     * Track search
     */
    async trackSearch(query: string, resultCount: number, metadata?: Record<string, any>): Promise<void> {
        await this.trackEvent({
            category: 'Search',
            action: 'search',
            label: query,
            value: resultCount,
            metadata: {
                search_query: query,
                result_count: resultCount,
                ...metadata,
            },
        });
    }

    /**
     * Track file download
     */
    async trackDownload(fileName: string, fileType: string, metadata?: Record<string, any>): Promise<void> {
        await this.trackEvent({
            category: 'Download',
            action: 'download',
            label: fileName,
            metadata: {
                file_name: fileName,
                file_type: fileType,
                ...metadata,
            },
        });
    }

    /**
     * Track button click
     */
    async trackButtonClick(buttonName: string, location: string, metadata?: Record<string, any>): Promise<void> {
        await this.trackEvent({
            category: 'Button',
            action: 'click',
            label: buttonName,
            metadata: {
                button_name: buttonName,
                location,
                ...metadata,
            },
        });
    }

    /**
     * Track form submission
     */
    async trackFormSubmission(formName: string, success: boolean, metadata?: Record<string, any>): Promise<void> {
        await this.trackEvent({
            category: 'Form',
            action: 'submit',
            label: formName,
            value: success ? 1 : 0,
            metadata: {
                form_name: formName,
                success,
                ...metadata,
            },
        });
    }

    /**
     * Track error
     */
    async trackError(errorMessage: string, errorType: string, metadata?: Record<string, any>): Promise<void> {
        await this.trackEvent({
            category: 'Error',
            action: errorType,
            label: errorMessage,
            metadata: {
                error_message: errorMessage,
                error_type: errorType,
                ...metadata,
            },
        });
    }
}
